import { getTRPCClient, syncKey } from "@repo/api";
import {
  type ChangeDeviceDefinitionAvailabilityInput,
  type CorrectDeviceDefinitionInput,
  changeDeviceDefinitionAvailabilityInputSchema,
  correctDeviceDefinitionInputSchema,
  type DefineDeviceDefinitionInput,
  defineDeviceDefinitionInputSchema,
  type ReviseDeviceDefinitionInput,
  reviseDeviceDefinitionInputSchema,
} from "@repo/api/schemas";
import { mintEntityId } from "@repo/domain";
import { canonicalBase, canonicalUrl } from "@repo/fhir-systems";
import { eq, ilike, max, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import type { CatalogValueDto } from "../fhir/catalog-value";
import type { DeviceDefinitionRow } from "../fhir/types";
import { memberId, optimisticDeviceDefinitionRow } from "../lib/device-definition-optimistic";
import { snapshotOfDevice } from "./definition-revision-snapshot";
import { buildVersionHistory, type DefinitionVersionHistoryDto } from "./definition-version-history";
import { projectDeviceDefinitionRow } from "./device-definition-projection";

const DEVICE_DEFINITION_COLLECTION = "deviceDefinition";

/** Every device definition canonical this app mints sits directly under this base. */
export const DEVICE_DEFINITION_CANONICAL_BASE = canonicalBase("DeviceDefinition");

export type DeviceDefinitionAvailability = "Active" | "Inactive";

/** A device parameter's declared value, as the shared catalog-value storage expresses it. */
export type DeviceParameterValueDto = CatalogValueDto;

/** Product-model facts needed by protocol authoring. */
export interface DeviceDefinitionDto {
  definitionUrl: string;
  version: number;
  mintedAt: string;
  name: string;
  description?: string;
  availability: DeviceDefinitionAvailability;
  manufacturer?: string;
  modelNumber?: string;
  parameters: readonly { id: string; label: string; value: DeviceParameterValueDto }[];
}

export interface DeviceDefinitionFilters {
  availability?: DeviceDefinitionAvailability;
  search?: string;
}

export function useDeviceDefinition(definitionUrl: string | undefined): DeviceDefinitionDto | undefined {
  const collection = useCollection(DEVICE_DEFINITION_COLLECTION);
  const result = useLiveQuery({
    queryKey: [collection.id, "device-definition", definitionUrl ?? null],
    query: (query) => {
      if (!definitionUrl) return undefined;
      return (
        query
          .from({ definition: collection })
          .where(({ definition }) => eq(definition.deviceDefinitionUrl, definitionUrl))
          .orderBy(({ definition }) => definition.deviceDefinitionVersion, "desc")
          // Device parameter extensions are not expressible through declarative query operators.
          .fn.select(({ definition }) => projectDeviceDefinitionRow(definition))
          .findOne()
      );
    },
  });
  return result.data;
}

export interface DeviceDefinitionsResult {
  definitions: DeviceDefinitionDto[];
  isLoading: boolean;
  isReady: boolean;
}

export function useDeviceDefinitions(filters: DeviceDefinitionFilters = {}): DeviceDefinitionDto[] {
  return useDeviceDefinitionsResult(filters).definitions;
}

export function useDeviceDefinitionsResult(filters: DeviceDefinitionFilters = {}): DeviceDefinitionsResult {
  const collection = useCollection(DEVICE_DEFINITION_COLLECTION);
  const search = filters.search?.trim();
  const result = useLiveQuery({
    queryKey: [collection.id, "device-definitions", filters.availability ?? null, search ?? null],
    query: (query) => {
      const latestVersions = query
        .from({ candidate: collection })
        .groupBy(({ candidate }) => candidate.deviceDefinitionUrl)
        .select(({ candidate }) => ({
          url: candidate.deviceDefinitionUrl,
          version: max(candidate.deviceDefinitionVersion),
        }));
      const current = query
        .from({ definition: collection })
        .join({ latest: latestVersions }, ({ definition, latest }) => eq(definition.deviceDefinitionUrl, latest.url))
        .where(({ definition, latest }) => eq(definition.deviceDefinitionVersion, latest.version));
      const available = filters.availability
        ? current.where(({ definition }) => eq(definition.deviceDefinitionAvailability, filters.availability))
        : current;
      const matching = search
        ? available.where(({ definition }) => ilike(definition.deviceDefinitionName, `%${search}%`))
        : available;
      return (
        matching
          .orderBy(({ definition }) => definition.deviceDefinitionName, "asc")
          // Device parameter extensions are not expressible through declarative query operators.
          .fn.select(({ definition }) => projectDeviceDefinitionRow(definition))
      );
    },
  });
  return { definitions: result.data ?? [], isLoading: result.isLoading, isReady: result.isReady };
}

// ── write actions ────────────────────────────────────────────────────────────

const deviceDefinitions = () =>
  getDataStoreClient().getCollection<DeviceDefinitionRow, string>(DEVICE_DEFINITION_COLLECTION);

/** Mint once before dispatch so optimistic and authoritative writes share lineage identity. */
export function mintDeviceDefinitionUrl(): string {
  return canonicalUrl("DeviceDefinition", mintEntityId());
}

function currentRow(definitionUrl: string): DeviceDefinitionRow & { id: string } {
  const row = [...deviceDefinitions().state.values()]
    .filter((candidate) => candidate.deviceDefinitionUrl === definitionUrl)
    .sort((left, right) => right.deviceDefinitionVersion - left.deviceDefinitionVersion)[0];
  if (!row?.id) throw new Error(`Device kind '${definitionUrl}' is not available in the data store`);
  return { ...row, id: row.id };
}

type DeviceDefinitionContent = Pick<DefineDeviceDefinitionInput, "parameters">;

function optimisticDto(
  content: DeviceDefinitionContent,
  spine: Pick<DeviceDefinitionDto, "definitionUrl" | "version" | "mintedAt" | "name" | "availability"> & {
    description?: string;
    manufacturer?: string;
    modelNumber?: string;
  },
): DeviceDefinitionDto {
  let optionIndex = 0;
  return {
    ...spine,
    parameters: content.parameters.map((parameter, index) => ({
      id: memberId(parameter.id, "p", index),
      label: parameter.label,
      value:
        parameter.value.kind === "Choice"
          ? {
              ...parameter.value,
              options: parameter.value.options.map((option) => ({
                id: memberId(option.id, "o", optionIndex++),
                label: option.label,
              })),
            }
          : { ...parameter.value },
    })),
  };
}

export const defineDeviceDefinition = defineWrite<DefineDeviceDefinitionInput>({
  optimistic: (params) => {
    const input = defineDeviceDefinitionInputSchema.parse(params);
    deviceDefinitions().insert(
      optimisticDeviceDefinitionRow(
        optimisticDto(input, {
          definitionUrl: input.definitionUrl,
          version: 1,
          mintedAt: new Date().toISOString(),
          name: input.name,
          ...(input.description === undefined ? {} : { description: input.description }),
          ...(input.manufacturer === undefined ? {} : { manufacturer: input.manufacturer }),
          ...(input.modelNumber === undefined ? {} : { modelNumber: input.modelNumber }),
          availability: "Active",
        }),
      ),
    );
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.deviceDefinition.define.mutate(input, options);
  },
});

export const reviseDeviceDefinition = defineWrite<ReviseDeviceDefinitionInput>({
  optimistic: (params) => {
    const input = reviseDeviceDefinitionInputSchema.parse(params);
    const current = projectDeviceDefinitionRow(currentRow(input.definitionUrl));
    deviceDefinitions().insert(
      optimisticDeviceDefinitionRow(
        optimisticDto(input, {
          definitionUrl: current.definitionUrl,
          version: input.expectedVersion + 1,
          mintedAt: new Date().toISOString(),
          name: current.name,
          ...(current.description === undefined ? {} : { description: current.description }),
          // Unreachable from revise: the domain carries them across every revision.
          ...(current.manufacturer === undefined ? {} : { manufacturer: current.manufacturer }),
          ...(current.modelNumber === undefined ? {} : { modelNumber: current.modelNumber }),
          availability: current.availability,
        }),
      ),
    );
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.deviceDefinition.revise.mutate(input, options);
  },
});

/** Rebuild the stored row through the optimistic writer so nothing here knows an extension url. */
function reproject(definitionUrl: string, change: (dto: DeviceDefinitionDto) => DeviceDefinitionDto): void {
  const row = currentRow(definitionUrl);
  const next = optimisticDeviceDefinitionRow(change(projectDeviceDefinitionRow(row)), row);
  deviceDefinitions().update(syncKey(row), (draft) => {
    Object.assign(draft, next);
    // A cleared description emits no note rather than an empty one, so
    // assigning over the draft would leave the stored one behind.
    if (next.note === undefined) delete draft.note;
  });
}

export const correctDeviceDefinition = defineWrite<CorrectDeviceDefinitionInput>({
  optimistic: (params) => {
    const input = correctDeviceDefinitionInputSchema.parse(params);
    reproject(input.definitionUrl, (dto) => ({
      ...dto,
      ...(input.name === undefined ? {} : { name: input.name }),
      ...(input.description === undefined ? {} : { description: input.description ?? undefined }),
    }));
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.deviceDefinition.correct.mutate(input, options);
  },
});

function availabilityAction(availability: DeviceDefinitionAvailability) {
  return defineWrite<ChangeDeviceDefinitionAvailabilityInput>({
    optimistic: (params) => {
      const input = changeDeviceDefinitionAvailabilityInputSchema.parse(params);
      reproject(input.definitionUrl, (dto) => ({ ...dto, availability }));
    },
    persist: async (input, options) => {
      const mutation =
        availability === "Active"
          ? getTRPCClient().catalog.deviceDefinition.activate
          : getTRPCClient().catalog.deviceDefinition.deactivate;
      await mutation.mutate(input, options);
    },
  });
}

export const activateDeviceDefinition = availabilityAction("Active");
export const deactivateDeviceDefinition = availabilityAction("Inactive");

/**
 * Every revision of one device definition lineage, newest first. Its own hook rather
 * than a branch of `useDefinitionVersionHistory`: that query reads `url` and
 * `version`, and this resource type spends both of those words on something
 * else, so the lineage is found through the derived columns instead. The diffing
 * is the shared one.
 */
export function useDeviceDefinitionVersionHistory(
  definitionUrl: string | undefined,
): DefinitionVersionHistoryDto | undefined {
  const collection = useCollection(DEVICE_DEFINITION_COLLECTION);
  const result = useLiveQuery({
    queryKey: [collection.id, "device-definition-version-history", definitionUrl ?? null],
    query: (query) => {
      if (!definitionUrl) return undefined;
      return (
        query
          .from({ definition: collection })
          .where(({ definition }) => eq(definition.deviceDefinitionUrl, definitionUrl))
          .orderBy(({ definition }) => definition.deviceDefinitionVersion, "desc")
          // Parameters live in extensions, which no declarative operator reaches.
          .fn.select(({ definition }) => ({
            resourceId: definition.id ?? "",
            author: definition.meta?.author?.display,
            dto: projectDeviceDefinitionRow(definition),
          }))
      );
    },
  });
  const revisions = result.data;
  if (!definitionUrl || !revisions?.length) return undefined;
  return buildVersionHistory(
    "device",
    definitionUrl,
    revisions.map(({ resourceId, author, dto }) => ({
      resourceId,
      version: String(dto.version),
      authoredAt: dto.mintedAt,
      ...(author ? { author } : {}),
      status: dto.availability === "Active" ? "active" : "retired",
      snapshot: snapshotOfDevice(dto),
    })),
  );
}
